<template>
    <section class="overview-graph">
        <div class="comp_scroller">
            <div class="comp_content">
                <div class="wrp_comp_body">
                    <h1>{{ translate ? $t(label) : label }}</h1>
                    <div class="totals" style="margin-bottom: 10px">
                        <OverviewGraphChange :diff-percent="diffPercent"
                                             v-if="typeof diffPercent === 'number' && Number.isFinite(diffPercent)"/>
                        <OverviewGraphValue :value="value" :unit="unit" v-if="typeof value === 'number'"/>
                    </div>
                    <div style="margin-bottom: 18px; display: flex;">
                        <div class="labeled-toggle-button">
                            <div :class="{item: true, selected: !forecast}"
                                 v-on:click="forecast = false">{{ $t('history') }}
                            </div>
                            <div class="delimiter"/>
                            <div :class="{item: true, selected: forecast}"
                                 v-on:click="forecast = true">{{ $t('forecast') }}
                            </div>
                        </div>
                    </div>
                    <ul class="x-axis-bookmarks">
                        <li v-for="i in xAxisBookmarks">
                            <a v-on:click="amount = i.amount; amountUnit=i.unit"
                               :style="{'text-decoration': amount === i.amount && amountUnit === i.unit?'underline':'none', 'cursor': 'pointer'}">{{
                                    i.label
                                }}</a>
                        </li>
                    </ul>
                    <div :class="{'state-loading':!data}">
                        <template v-if="!!data">
                            <Bar :data="data" v-if="field === 'booking_window'" :unit="unit" :y-format="yFormat"
                                 :x-format="xFormat" :y-max="yMax" :tooltip-format="tooltipFormat"/>
                            <LineNew :data="data" v-else :unit="unit" :y-format="yFormat" :x-format="xFormat"/>
                        </template>
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<script>

import OverviewGraphChange from '../part/ui/overview-graph-change';
import OverviewGraphValue from '../part/ui/overview-graph-value';
import LineNew from '../part/chart/line-new';
import moment from 'moment';
import axios from 'axios';
import Bar from '../part/chart/bar.vue';

export default {
    name: 'overview-graph',
    props: ['label', 'translate', 'field'],
    components: {Bar, LineNew, OverviewGraphValue, OverviewGraphChange},
    data() {
        return {
            diffPercent: undefined,
            value: undefined,
            unit: '',
            xFormat: undefined,
            yFormat: undefined,
            yMax: undefined,
            tooltipFormat: undefined,
            xAxisBookmarks: [
                {label: '7 ' + this.$t('days'), amount: 7, unit: 'd'},
                {label: '30 ' + this.$t('days'), amount: 30, unit: 'd'},
                {label: '3 ' + this.$t('graph_months'), amount: 3, unit: 'M'},
                {label: '6 ' + this.$t('graph_months'), amount: 6, unit: 'M'},
                {label: '1 ' + this.$t('graph_year'), amount: 1, unit: 'y'},
            ],
            amount: 7,
            amountUnit: 'd',
            data: undefined,
            forecast: false
        };
    },
    methods: {
        formatDays: function (days, value = days) {
            let str;
            if (value === 1) {
                str = this.$t('days_1')
            } else if (value >= 2 && value <= 4) {
                str = this.$t('days_2')
            } else {
                str = this.$t('days')
            }
            return days + ' ' + str.toLowerCase()
        },
        fetchData: function () {
            this.data = undefined

            const dateFrom =
                this.forecast ?
                    moment() :
                    moment().startOf('day').subtract(this.amount, this.amountUnit)

            const dateTo =
                this.forecast ?
                    moment().startOf('day').add(this.amount, this.amountUnit) :
                    moment()

            if (this.field === 'booking_window') {
                axios({
                    data: `{
                        bookingWindow90(date_from: "${dateFrom.format('YYYY-MM-DD')}", date_to: "${dateTo.format('YYYY-MM-DD')}") {
                            window_start
                            window_end,
                            reservation_count
                        }
                        averageBookingWindow(date_from: "${dateFrom.format('YYYY-MM-DD')}", date_to: "${dateTo.format('YYYY-MM-DD')}")
                        basicInfo {
                          national_currency {
                            symbol
                          }
                        }
                    }`
                }).then(response => {
                    const {
                        data: {
                            data: {
                                bookingWindow90,
                                averageBookingWindow
                            }
                        }
                    } = response

                    this.value = averageBookingWindow !== null ? Number(averageBookingWindow) : null

                    const data = bookingWindow90.map(i => ({
                        label: i.window_start === 0 ? '0' : i.window_start + (i.window_end ? '-' + i.window_end : '+'),
                        value: i.reservation_count
                    }))

                    const totalReservationsCount = data.map(d => d.value).reduce((a, b) => a + b, 0)

                    this.data = data.map(d => ({
                        label: d.label,
                        value: d.value / totalReservationsCount
                    }))

                    // Simulace forbidden chyby, pokud mi API vrátí null
                    if (this.data.some(s => s.value === null)) {
                        throw {code: 403}
                    }

                    this.unit = this.$t('days')
                    this.tooltipFormat = (val) => (Number(val) * 100).toFixed() + ' %'
                    this.xFormat = (val) => this.formatDays(val, val.split(/[^0-9]+/).slice(-1)[0])
                    this.yFormat = (val) => (Number(val) * 100).toFixed() + ' %'
                    this.yMax = 1
                    this.diffPercent = null
                }).catch(error => {
                    this.showError(error)
                });
            } else {
                axios({
                    data: `{
                        dailyStatistics(date_from: "${dateFrom.format('YYYY-MM-DD')}", date_to: "${dateTo.format('YYYY-MM-DD')}") {
                            date,
                            ${this.field}
                        }
                        basicInfo {
                          national_currency {
                            symbol
                          }
                        }
                    }`
                }).then(response => {
                    const {
                        data: {
                            data: {
                                dailyStatistics,
                                basicInfo: {national_currency: {symbol: currencySymbol}}
                            }
                        }
                    } = response

                    const rawValue = dailyStatistics[dailyStatistics.length - 1][this.field]
                    this.value = rawValue !== null ? Number(rawValue) : null
                    this.data = dailyStatistics.map(i => ({
                        label: moment(i.date).format('D.M.'),
                        value: i[this.field]
                    }))

                    // Simulace forbidden chyby, pokud mi API vrátí null
                    if (this.data.some(s => s.value === null)) {
                        throw {code: 403}
                    }

                    this.unit = this.field === 'alos' ? this.$t('days') : currencySymbol;

                    if(this.field === 'alos') {
                        this.yFormat = val => this.formatDays(val)
                    }

                    const previous = Number(dailyStatistics[0][this.field])
                    this.diffPercent = this.value !== null ? (this.value / previous - 1) * 100 : null
                }).catch(error => {
                    this.showError(error)
                });
            }
        }
    },
    mounted() {
        this.fetchData()
    },
    watch: {
        amount: function (val) {
            this.fetchData()
        },
        amountUnit: function (val) {
            this.fetchData()
        },
        forecast: function (val) {
            this.fetchData()
        },
    }
}
</script>



// WEBPACK FOOTER //
// src/templates/comp/overview-graph.vue